import { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from "@react-google-maps/api";
import { MapPin, Store, Truck, CheckCircle, Loader2 } from "lucide-react";

const LIBRARIES = ["places"];
const MAP_STYLE = { width: "100%", height: "280px", borderRadius: "12px" };
const INDIA_DEFAULT = { lat: 18.5204, lng: 73.8567 };

const inputCls =
  "w-full px-3 py-2.5 bg-white/[0.04] border border-white/[0.10] text-white rounded-xl focus:border-[#FF6B35]/50 focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/20 transition-all text-sm placeholder-gray-600";

const LocationSection = ({
  serviceMode,
  setServiceMode,
  userLocation,
  setUserLocation,
  address,
  setAddress,
  locationError,
  setLocationError,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries: LIBRARIES,
  });

  const [mapCenter, setMapCenter] = useState(INDIA_DEFAULT);
  const [markerPos, setMarkerPos] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const autocompleteRef = useRef(null);

  const isDoorstep = serviceMode === "doorstep";

  const applyCoords = useCallback(
    (lat, lng) => {
      const coords = { lat, lng };
      setMapCenter(coords);
      setMarkerPos(coords);
      setUserLocation(coords);
      setLocationError("");
    },
    [setUserLocation, setLocationError]
  );

  const handleAddressField = (key, value) =>
    setAddress((prev) => ({ ...prev, [key]: value }));

const handleGPS = useCallback(() => {
  setGpsLoading(true);

  // ==========================================
  // REACT NATIVE WEBVIEW
  // ==========================================
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "REQUEST_LOCATION",
      })
    );

    return;
  }

  // ==========================================
  // NORMAL WEBSITE / BROWSER
  // ==========================================
  if (!navigator.geolocation) {
    setLocationError(
      "Geolocation is not supported by your browser."
    );

    setGpsLoading(false);
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      applyCoords(
        pos.coords.latitude,
        pos.coords.longitude
      );

      setGpsLoading(false);
    },

    () => {
      setLocationError(
        "Unable to access location. Please allow permission or search manually."
      );

      setGpsLoading(false);
    },

    {
      enableHighAccuracy: true,
      timeout: 12000,
    }
  );
}, [applyCoords, setLocationError]);

// ==========================================
// RECEIVE LOCATION FROM REACT NATIVE WEBVIEW
// ==========================================
useEffect(() => {
  const handleNativeLocation = (event) => {
    const { latitude, longitude } = event.detail;

    applyCoords(latitude, longitude);

    setGpsLoading(false);
  };

  const handleNativeLocationError = (event) => {
    setLocationError(
      event.detail?.message ||
        "Unable to get current location."
    );

    setGpsLoading(false);
  };

  window.addEventListener(
    "OKMECHANICS_LOCATION",
    handleNativeLocation
  );

  window.addEventListener(
    "OKMECHANICS_LOCATION_ERROR",
    handleNativeLocationError
  );

  return () => {
    window.removeEventListener(
      "OKMECHANICS_LOCATION",
      handleNativeLocation
    );

    window.removeEventListener(
      "OKMECHANICS_LOCATION_ERROR",
      handleNativeLocationError
    );
  };
}, [applyCoords, setLocationError]);

  const handleMapClick = useCallback(
    (e) => applyCoords(e.latLng.lat(), e.latLng.lng()),
    [applyCoords]
  );

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;
    applyCoords(place.geometry.location.lat(), place.geometry.location.lng());
    const comps = place.address_components || [];
    const get = (type) => comps.find((c) => c.types.includes(type))?.long_name || "";
    setAddress((prev) => ({
      ...prev,
      area: prev.area || get("sublocality_level_1") || get("sublocality") || get("locality"),
      city: prev.city || get("administrative_area_level_2") || get("locality"),
      pincode: prev.pincode || get("postal_code"),
      landmark: prev.landmark || place.name || "",
      fullAddress: prev.fullAddress || place.formatted_address || "",
    }));
  }, [applyCoords, setAddress]);

  const handleMarkerDrag = useCallback(
    (e) => applyCoords(e.latLng.lat(), e.latLng.lng()),
    [applyCoords]
  );

  const locationLabel = isDoorstep
    ? "Pickup Address — mechanic will come here"
    : "Your Location — to find the nearest service center";

  return (
    <div className="space-y-5">
      {/* Service Mode Toggle */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { key: "center", Icon: Store, title: "Visit Service Center", desc: "Drop your bike at store" },
          { key: "doorstep", Icon: Truck, title: "Doorstep Service", desc: "Mechanic comes to you (+₹100)" },
        ].map(({ key, Icon, title, desc }) => (
          <button
            key={key}
            type="button"
            onClick={() => setServiceMode(key)}
            className={[
              "relative p-3 sm:p-4 rounded-xl border text-left transition-all duration-200",
              serviceMode === key
                ? "border-[#FF6B35] bg-[#FF6B35]/10 shadow-lg shadow-[#FF6B35]/10"
                : "border-white/[0.08] bg-white/[0.03] hover:border-white/20",
            ].join(" ")}
          >
            {serviceMode === key && (
              <CheckCircle className="absolute top-2 right-2 h-4 w-4 text-[#FF6B35]" />
            )}
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${serviceMode === key ? "bg-[#FF6B35]" : "bg-white/[0.08]"}`}>
                <Icon className={`h-4 w-4 ${serviceMode === key ? "text-white" : "text-gray-400"}`} />
              </div>
              <div>
                <p className="text-xs font-bold text-white leading-tight">{title}</p>
                <p className="text-[10px] text-gray-500">{desc}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Context Label */}
      <div className="px-3 py-2.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl text-xs text-[#FF6B35] font-medium flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
        {locationLabel}
      </div>

      {/* Map Block */}
      {loadError ? (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
          ⚠️ Google Maps failed to load. Check your API key.
        </div>
      ) : !isLoaded ? (
        <div className="h-40 bg-white/[0.03] border border-white/[0.06] rounded-xl flex items-center justify-center text-gray-600 text-sm gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading map…
        </div>
      ) : (
        <div className="space-y-3">
          <Autocomplete
            onLoad={(ref) => (autocompleteRef.current = ref)}
            onPlaceChanged={handlePlaceChanged}
            options={{ componentRestrictions: { country: "in" } }}
          >
            <input
              type="text"
              placeholder="Search your area, street or landmark…"
              className={inputCls}
            />
          </Autocomplete>

          <button
            type="button"
            onClick={handleGPS}
            disabled={gpsLoading}
            className="w-full py-2.5 bg-[#FF6B35] hover:bg-[#E85520] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {gpsLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Getting your location…
              </>
            ) : (
              <>
                <MapPin className="h-4 w-4" />
                Use My Current Location
              </>
            )}
          </button>

          <div className="rounded-xl overflow-hidden border border-white/[0.08]">
            <GoogleMap
              mapContainerStyle={MAP_STYLE}
              center={mapCenter}
              zoom={15}
              onClick={handleMapClick}
              options={{ disableDefaultUI: false, zoomControl: true, streetViewControl: false }}
            >
              {markerPos && (
                <Marker
                  position={markerPos}
                  draggable
                  onDragEnd={handleMarkerDrag}
                  title="Drag to adjust"
                />
              )}
            </GoogleMap>
          </div>

          <p className="text-xs text-gray-600 text-center">
            Click on the map or drag the pin to set your exact location.
          </p>

          {userLocation?.lat && userLocation?.lng && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-xl text-xs text-green-400 font-medium">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              Location set: {Number(userLocation.lat).toFixed(5)}, {Number(userLocation.lng).toFixed(5)}
            </div>
          )}

          {locationError && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-xs text-red-400">
              ⚠️ {locationError}
            </div>
          )}
        </div>
      )}

      {/* Address Fields */}
      <div className="space-y-3 pt-3 border-t border-white/[0.06]">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
          {isDoorstep ? "Pickup Address Details" : "Address Details (optional for center visits)"}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Flat / House No {isDoorstep && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              placeholder="e.g., Flat 101, Tower A"
              value={address.flatNo}
              onChange={(e) => handleAddressField("flatNo", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Area / Locality {isDoorstep && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              placeholder="e.g., Koregaon Park"
              value={address.area}
              onChange={(e) => handleAddressField("area", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Landmark</label>
            <input
              type="text"
              placeholder="e.g., Near City Mall"
              value={address.landmark}
              onChange={(e) => handleAddressField("landmark", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              City {isDoorstep && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              placeholder="e.g., Pune"
              value={address.city}
              onChange={(e) => handleAddressField("city", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Pincode {isDoorstep && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              placeholder="6-digit pincode"
              value={address.pincode}
              onChange={(e) => handleAddressField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Full Address {isDoorstep && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              placeholder="Complete address"
              value={address.fullAddress}
              onChange={(e) => handleAddressField("fullAddress", e.target.value)}
              className={inputCls}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSection;
